import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PhoneCall, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

// Admin-only tuning panel for the tenant-wide dialer cadence.
// Every rep on this workspace inherits these values on next Dialer
// mount — one place, one source of truth.
export default function DialerSettingsCard() {
  const [prefs, setPrefs]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [loading, setLoad]  = useState(true);

  const load = async () => {
    setLoad(true);
    try {
      const { data } = await api.get("/tenant/dialer-prefs");
      setPrefs(data?.dialer_prefs || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dialer prefs");
    } finally { setLoad(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/tenant/dialer-prefs", { dialer_prefs: prefs });
      setPrefs(data?.dialer_prefs || prefs);
      toast.success("Dialer settings saved for the whole team");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed (admin only)");
    } finally { setBusy(false); }
  };

  if (loading || !prefs) {
    return (
      <div className="card-flat p-6 mt-6 reveal reveal-4">
        <Loader2 size={16} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  const set = (k) => (v) => setPrefs((p) => ({ ...p, [k]: v }));
  const num = (k) => (e) => set(k)(Number(e.target.value));
  const str = (k) => (e) => set(k)(e.target.value);

  return (
    <div className="card-flat p-6 mt-6 reveal reveal-4" data-testid="dialer-settings-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center shrink-0">
          <PhoneCall size={16} />
        </div>
        <div>
          <div className="font-display text-lg font-bold">Dialer cadence</div>
          <p className="text-xs text-muted-foreground mt-1">
            Kill silence between calls. Tune ring time, auto-dispose,
            ringback tone and warm-transfer. Applies to every rep in
            this workspace on their next Dialer page load.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NumField
          label="Inter-call gap (ms)"
          hint="Pause between dispo click and next dial. Lower = more dials/hr."
          value={prefs.inter_call_gap_ms} onChange={num("inter_call_gap_ms")}
          min={0} max={10000} testid="dp-gap"
        />
        <NumField
          label="Auto-dispose after (sec)"
          hint="If rep doesn't pick a chip, auto-log 'no answer' and dial next. 0 = disabled."
          value={prefs.auto_dispose_delay_sec} onChange={num("auto_dispose_delay_sec")}
          min={0} max={120} testid="dp-auto-dispose"
        />
        <NumField
          label="Max ring seconds"
          hint="Hang up automatically if the seller doesn't answer in this window."
          value={prefs.max_ring_sec} onChange={num("max_ring_sec")}
          min={5} max={120} testid="dp-max-ring"
        />
        <NumField
          label="Wrap-up timeout (sec)"
          hint="Force-close the dispo modal after this many seconds."
          value={prefs.wrap_timeout_sec} onChange={num("wrap_timeout_sec")}
          min={0} max={300} testid="dp-wrap"
        />
        <TextField
          label="Warm-transfer number"
          hint="Acquisitions manager number. Rep taps Transfer → this number joins the call."
          value={prefs.warm_transfer_number || ""} onChange={str("warm_transfer_number")}
          placeholder="+13055551234" testid="dp-warm"
        />
        <TextField
          label="Auto-dispose key"
          hint="Which disposition auto-fires when the countdown expires."
          value={prefs.auto_dispose_key || "no-answer"} onChange={str("auto_dispose_key")}
          placeholder="no-answer" testid="dp-auto-key"
        />
      </div>

      <div className="mt-5 space-y-3">
        <ToggleRow
          label="Play ringback tone to rep"
          hint="Rep hears US-style ringing so they don't camp on silence."
          checked={!!prefs.ringback_enabled}
          onCheckedChange={set("ringback_enabled")}
          testid="dp-ringback"
        />
        <ToggleRow
          label="Answering-machine detection"
          hint="Skip past voicemail greetings. Turn off if your carrier is fussy."
          checked={!!prefs.amd_enabled}
          onCheckedChange={set("amd_enabled")}
          testid="dp-amd"
        />
        <ToggleRow
          label="Preload next lead"
          hint="Hydrate the next lead in memory during wrap so the dial fires instantly."
          checked={!!prefs.preload_next}
          onCheckedChange={set("preload_next")}
          testid="dp-preload"
        />
        <ToggleRow
          label="Persistent softphone session"
          hint="Keep the SIP connection alive across calls (no re-register)."
          checked={!!prefs.persistent_session}
          onCheckedChange={set("persistent_session")}
          testid="dp-session"
        />
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          onClick={save}
          disabled={busy}
          className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          data-testid="dp-save-btn"
        >
          {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Save size={13} className="mr-2" />}
          Save for the whole team
        </Button>
      </div>
    </div>
  );
}

function NumField({ label, hint, value, onChange, min, max, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold">{label}</Label>
      <Input type="number" value={value} onChange={onChange} min={min} max={max}
             className="mt-1 h-9 text-sm font-mono" data-testid={testid} />
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function TextField({ label, hint, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold">{label}</Label>
      <Input value={value} onChange={onChange} placeholder={placeholder}
             className="mt-1 h-9 text-sm" data-testid={testid} />
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function ToggleRow({ label, hint, checked, onCheckedChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[var(--app-line)] last:border-b-0">
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testid} />
    </div>
  );
}
